import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AirplaneTilt, Phone } from "@phosphor-icons/react";
import { api, formatPrice, trackRouteEvent } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";

function RouteCard({ route }) {
  const hasPrice = typeof route.price === "number";
  return (
    <Link
      to={`/flights/${route.slug}`}
      data-testid={`popular-route-${route.slug}`}
      onClick={() => trackRouteEvent("click", route.origin, route.destination)}
      className="group bg-white border border-[#E0DDD6] p-5 md:p-5 flex flex-col lift transition-colors hover:border-[#C29B62]"
    >
      <p className="tcp-overline text-[#C29B62] mb-2">{route.region}</p>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <p className="font-serif text-xl md:text-2xl tracking-tight text-[#1C1C1C] leading-tight">
          {route.origin_city}
          <span className="text-[#A09E9A] mx-2">→</span>
          <em className="italic">{route.destination_city}</em>
        </p>
      </div>
      <p className="text-xs tracking-[0.16em] uppercase text-[#666460] mb-4">
        {route.origin} · {route.destination}{route.airline_name ? ` · ${route.airline_name}` : ""}
      </p>
      <div className="mt-auto flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-[#666460]">{hasPrice ? "From" : ""}</p>
          {hasPrice ? (
            <p className="font-serif text-3xl md:text-4xl tracking-tighter" style={{ color: "#C29B62" }}>
              {formatPrice(route.price, route.currency)}
              <span className="text-xs text-[#666460] font-sans ml-1">one-way</span>
            </p>
          ) : (
            <p className="font-serif text-2xl md:text-3xl tracking-tight text-[#1C1C1C]">Call for fare</p>
          )}
        </div>
        <span className="text-xs tracking-[0.16em] uppercase text-[#C29B62] group-hover:text-[#A88550] inline-flex items-center gap-1 pb-1 border-b border-transparent group-hover:border-[#A88550] transition-colors">
          View route <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white border border-[#E0DDD6] p-6 md:p-7 animate-pulse">
      <div className="h-3 w-20 bg-[#E0DDD6] mb-3" />
      <div className="h-6 w-40 bg-[#E0DDD6] mb-2" />
      <div className="h-3 w-32 bg-[#E0DDD6] mb-5" />
      <div className="h-8 w-24 bg-[#E0DDD6]" />
    </div>
  );
}

export default function PopularRoutes() {
  const [routes, setRoutes] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/products/flights/popular-routes")
      .then((r) => setRoutes(r.data?.routes || []))
      .catch(() => setError(true));
  }, []);

  // Hide the entire section if the API fails — it's a marketing surface, not a critical path.
  if (error) return null;

  return (
    <section className="hidden md:block bg-[#0A0A0B] text-[#F7F5F0] py-12 md:py-16 px-6 md:px-12 lg:px-24" data-testid="popular-routes-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
        <div className="max-w-2xl">
          <p className="tcp-overline text-[#C29B62] mb-3">Popular Routes</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none">
            The city pairs <em className="italic text-[#C29B62]">we live and breathe.</em>
          </h2>
          <p className="text-[#A09E9A] mt-3 max-w-xl leading-relaxed">
            Eight of our most-booked corridors, refreshed daily with the lowest fare we can
            find from the airlines. Each route opens a dedicated page with seasonal tips and
            current premium-cabin options.
          </p>
        </div>
        <a
          href={`tel:${PHONE_TEL}`}
          data-testid="popular-routes-call"
          className="self-start md:self-end inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#C29B62] hover:text-[#F7F5F0] border-b border-[#C29B62] hover:border-[#F7F5F0] pb-1 transition-colors whitespace-nowrap"
        >
          <Phone size={14} weight="fill" /> Call {PHONE_DISPLAY}
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {routes === null
          ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
          : routes.map((r) => <RouteCard key={r.slug} route={r} />)}
      </div>

      <div className="mt-6 flex items-center gap-3 text-xs tracking-[0.18em] uppercase text-[#A09E9A]">
        <AirplaneTilt size={14} className="text-[#C29B62]" weight="fill" />
        <span>Fares are live · refreshed every 6 hours · taxes & fees included</span>
      </div>
    </section>
  );
}
